import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/styles.css';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';

const ModificarMateria = () => {
  // Combos
  const [grupos, setGrupos] = useState([]); // [{IdGrupo, Grado, CicloEscolar, Status, ...}]
  const [materias, setMaterias] = useState([]); // [{IdMateria, Nombre, ...}]
  const [categorias, setCategorias] = useState([]); // [{IdCategoriaMateria, Descripcion}]

  // Selecciones / campos
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(''); // IdGrupo
  const [materiaSeleccionada, setMateriaSeleccionada] = useState(''); // IdMateria
  const [idMateria, setIdMateria] = useState(''); // deshabilitado
  const [idCategoriaMateria, setIdCategoriaMateria] = useState(''); // select
  const [nombreMateria, setNombreMateria] = useState(''); // input
  const [grado, setGrado] = useState(''); // select 1-6
  const [status, setStatus] = useState('A'); // 'A' | 'I'
  const [nombreOriginal, setNombreOriginal] = useState('');

  const [saving, setSaving] = useState(false);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const sendJsonWithFallback = useCallback(async (path, payload, method = 'POST') => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la solicitud ${method} a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith('<')) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        if (!trimmed) return { text: '', parsed: null, url };

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return { text: parsed?.mensaje || parsed?.message || trimmed, parsed, url };
          } catch (parseErr) {
            console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
          }
        }

        return { text: trimmed, parsed: null, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al enviar ${method} a ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  const cargarGruposCombo = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/grupos');
      setGrupos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar grupos:', err);
      await Swal.fire('Error', err.message || 'No se pudieron cargar los grupos.', 'error');
    }
  }, [fetchJsonWithFallback]);

  const cargarMateriasCombo = useCallback(async (idGrupo) => {
    if (!idGrupo) {
      setMaterias([]);
      return [];
    }

    try {
      const data = await fetchJsonWithFallback(`/materias/por-grupo/${idGrupo}`);
      const arreglo = Array.isArray(data) ? data : [];
      setMaterias(arreglo);
      return arreglo;
    } catch (err) {
      console.error('Error al cargar materias por grupo:', err);
      setMaterias([]);
      await Swal.fire('Error', err.message || 'No se pudieron cargar las materias del grupo seleccionado.', 'error');
      return [];
    }
  }, [fetchJsonWithFallback]);

  const cargarCategoriasCombo = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/categorias/obtener-descripciones');
      setCategorias(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      await Swal.fire('Error', err.message || 'No se pudieron cargar las categorías.', 'error');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarGruposCombo();
    cargarCategoriasCombo();
  }, [cargarCategoriasCombo, cargarGruposCombo]);

  const obtenerDatosMateriaSeleccionada = useCallback(async (id) => {
    if (!id) return;

    try {
      const m = await fetchJsonWithFallback(`/materias/por-id/${id}`);

      if (!m) {
        setIdMateria('');
        setIdCategoriaMateria('');
        setNombreMateria('');
        setNombreOriginal('');
        setGrado('');
        setStatus('A');
        await Swal.fire('Advertencia', 'No se encontró la materia seleccionada.', 'warning');
        return;
      }

      setIdMateria(m.IdMateria ?? '');
      setIdCategoriaMateria(String(m.IdCategoriaMateria ?? ''));
      setNombreMateria(m.Nombre ?? '');
      setNombreOriginal(m.Nombre ?? '');
      setGrado(String(m.GradoAsignado ?? ''));
      setStatus(String(m.Status ?? 'A'));
    } catch (err) {
      console.error('Error al obtener datos de materia:', err);
      await Swal.fire('Error', 'No se pudo obtener la información de la materia.', 'error');
    }
  }, [fetchJsonWithFallback]);

  const onChangeGrupoCombo = async (e) => {
    const id = e.target.value;
    setGrupoSeleccionado(id);
    setMateriaSeleccionada('');
    setIdMateria('');
    setIdCategoriaMateria('');
    setNombreMateria('');
    setNombreOriginal('');
    setGrado('');
    setStatus('A');
    setMaterias([]);
    await cargarMateriasCombo(id);
  };

  const onChangeMateriaCombo = async (e) => {
    const id = e.target.value;
    setMateriaSeleccionada(id);
    await obtenerDatosMateriaSeleccionada(id);
  };

  const guardarCambiosMateria = async () => {
    const NombreOriginal = nombreOriginal;
    const IdCategoriaMateriaParsed = parseInt(idCategoriaMateria, 10);
    const GradoAsignadoParsed = parseInt(grado, 10);
    const NuevoNombre = nombreMateria.trim();
    const Status = status;

    if (!grupoSeleccionado) {
      await Swal.fire('Campos vacíos', 'Selecciona primero un grupo.', 'warning');
      return;
    }

    if (!materiaSeleccionada) {
      await Swal.fire('Campos vacíos', 'Selecciona una materia del grupo.', 'warning');
      return;
    }

    if (!NombreOriginal || !IdCategoriaMateriaParsed || !NuevoNombre || !GradoAsignadoParsed || !Status) {
      await Swal.fire('Campos vacíos', 'Por favor completa todos los campos.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const { parsed: payload } = await sendJsonWithFallback('/materias/modificar', {
        NombreOriginal,
        IdCategoriaMateria: IdCategoriaMateriaParsed,
        NuevoNombre,
        GradoAsignado: GradoAsignadoParsed,
        Status
      }, 'PUT');

      if (payload && payload.error) {
        throw new Error(payload.error);
      }

      await Swal.fire('Éxito', 'Materia modificada correctamente.', 'success');

      if (grupoSeleccionado) {
        const materiasActualizadas = await cargarMateriasCombo(grupoSeleccionado);
        const materiaIdActual = materiaSeleccionada || idMateria;
        const materiaRenovada =
          materiasActualizadas.find((m) => String(m.IdMateria) === String(materiaIdActual)) ||
          materiasActualizadas.find((m) => String(m.Nombre).toLowerCase() === NuevoNombre.toLowerCase());

        if (materiaRenovada) {
          const idSeleccion = String(materiaRenovada.IdMateria);
          setMateriaSeleccionada(idSeleccion);
          setIdMateria(idSeleccion);
          setNombreOriginal(materiaRenovada.Nombre ?? NuevoNombre);
          await obtenerDatosMateriaSeleccionada(idSeleccion);
        } else {
          setMateriaSeleccionada('');
          setIdMateria('');
          setNombreOriginal('');
          setNombreMateria('');
          setIdCategoriaMateria('');
          setGrado('');
          setStatus('A');
          await Swal.fire('Aviso', 'La materia ya no está asociada al grupo seleccionado o fue removida.', 'info');
        }
      }
    } catch (err) {
      console.error('Error al modificar materia:', err);
      await Swal.fire('Error', err.message || 'Hubo un problema al guardar los cambios.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setMateriaSeleccionada('');
    setIdMateria('');
    setIdCategoriaMateria('');
    setNombreMateria('');
    setNombreOriginal('');
    setGrado('');
    setStatus('A');
  };

  return (
    <div className="container mt-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
              <img src={logoInstituto} alt="Logo Instituto" className="logo" />
              <div className="flex-grow-1 text-center">
                <h4 className="mb-0">Instituto Silviano Carrillo</h4>
                <small className="text-muted fs-5">Modificar Materia</small>
              </div>
              <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Combo de grupos */}
      <div className="mb-4">
        <label htmlFor="comboGrupos" className="form-label">Selecciona el grupo:</label>
        <select
          id="comboGrupos"
          className="form-select"
          value={grupoSeleccionado}
          onChange={onChangeGrupoCombo}
        >
          <option value="" disabled>Selecciona un grupo</option>
          {grupos.map((g) => (
            <option key={g.IdGrupo} value={g.IdGrupo}>
              {`Grupo ${g.IdGrupo} • ${g.Grado ? `${g.Grado}º` : 'Sin grado'} • ${g.CicloEscolar || 'Sin ciclo'}`}
            </option>
          ))}
        </select>
      </div>

      {/* Combo de materias */}
      <div className="mb-4">
        <label htmlFor="comboMaterias" className="form-label">Selecciona la materia:</label>
        <select
          id="comboMaterias"
          className="form-select"
          value={materiaSeleccionada}
          onChange={onChangeMateriaCombo}
          disabled={!grupoSeleccionado || materias.length === 0}
        >
          <option value="" disabled>
            {grupoSeleccionado ? 'Selecciona una materia' : 'Primero elige un grupo'}
          </option>
          {materias.map((m) => (
            <option key={m.IdMateria} value={m.IdMateria}>{m.Nombre}</option>
          ))}
        </select>
        {grupoSeleccionado && materias.length === 0 && (
          <small className="text-muted">No hay materias asociadas a este grupo.</small>
        )}
      </div>

      {/* Formulario para modificar materia */}
      <form id="formModificarMateria" className="row g-3" onSubmit={(e) => e.preventDefault()}>
        <div className="col-md-3">
          <label htmlFor="idMateria" className="form-label">ID Materia</label>
          <input type="text" id="idMateria" className="form-control" value={idMateria} disabled />
        </div>

        <div className="col-md-4">
          <label htmlFor="idCategoriaMateria" className="form-label">Categoría de Materia</label>
          <select
            className="form-select"
            id="idCategoriaMateria"
            value={idCategoriaMateria}
            onChange={(e) => setIdCategoriaMateria(e.target.value)}
            disabled={!materiaSeleccionada}
          >
            <option value="" disabled>Selecciona una categoría</option>
            {categorias.map((c) => (
              <option key={c.IdCategoriaMateria} value={c.IdCategoriaMateria}>
                {c.Descripcion}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label htmlFor="nombreMateria" className="form-label">Nombre de la Materia</label>
          <input
            type="text"
            id="nombreMateria"
            className="form-control"
            placeholder="Ej: Matemáticas"
            value={nombreMateria}
            onChange={(e) => setNombreMateria(e.target.value)}
            disabled={!materiaSeleccionada}
          />
        </div>

        <div className="col-md-3">
          <label htmlFor="grado" className="form-label">Grado Asignado</label>
          <select
            className="form-select"
            id="grado"
            value={grado}
            onChange={(e) => setGrado(e.target.value)}
            disabled={!materiaSeleccionada}
          >
            <option value="" disabled>Selecciona un grado</option>
            <option value="1">1º</option>
            <option value="2">2º</option>
            <option value="3">3º</option>
            <option value="4">4º</option>
            <option value="5">5º</option>
            <option value="6">6º</option>
          </select>
        </div>

        <div className="col-md-3" style={{ display: 'none' }}>
          <label htmlFor="status" className="form-label">Status</label>
          <select
            className="form-select"
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={!materiaSeleccionada}
          >
            <option value="A">Activo</option>
            <option value="B">Baja</option>
          </select>
        </div>

        <div className="col-12 mt-4 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-success"
            id="btnGuardarCambios"
            onClick={guardarCambiosMateria}
            disabled={saving || !grupoSeleccionado || !materiaSeleccionada}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={resetForm}>
            Cancelar
          </button>
        </div>
      </form>

      {/* Botón salir */}
      <div className="mt-4 text-center">
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default ModificarMateria;
